import axios from "axios";

/**
 * Create a Sales Pipeline watch from a confirmed order request
 * (Alex item 4, 2026-10-06).
 *
 * "Watch for a supplier at this price": the client's ceiling becomes a buy
 * target on a Core List entry of type `pipeline`, so the daily digest reports
 * the morning a trade-group quote lands at or under it. No listing is
 * involved — the entry exists to answer "can we source this, and at what".
 *
 * Returns null without calling anything only when there is no product or no
 * store: there is nothing to watch, and the API would reject it anyway.
 *
 * NO BUDGET STILL POSTS, with `max_price: null`. It used to return here too,
 * so a sourcing request with the toggle on and no budget never reached the
 * Sales Pipeline, and nothing said so (Guillaume, 2026-10-13: "that should not
 * be a reason not to add into the sales pipeline"). The entry is made with no
 * target, and the backend leaves any earlier order's target in place.
 */
export const createBuyAlertWatch = async ({
  productId,
  storeIdM,
  data,
  requestId,
}) => {
  // The client's ceiling IS the watch price: at or under it leaves a margin,
  // above it does not. priceExpectation is the fallback for flows that collect
  // one number rather than a range.
  const raw = data?.maxBudget ?? data?.priceExpectation;

  // Budgets arrive as formatted strings ("HK$ 210,000.00"), so the symbol and
  // the grouping commas are stripped — but the SIGN is kept. Stripping
  // everything non-numeric turned -5 into 5, which would have set a positive
  // target from a negative budget instead of refusing it.
  const minor = Number(String(raw ?? "").replace(/[^0-9.-]/g, ""));

  // ⚠️ The form holds budgets in MINOR units. `maxBudget: 240000` is £2,400 —
  // RequestForm's own test says so ("shows the stored pence as major units").
  // `core_list_price_targets.max_price` is WHOLE units, so sending the raw
  // value set every target 100× too high: a £2,400 watch asked to alert at
  // £240,000, which every trade quote beats. Shipped that way on 2026-10-06
  // and caught on the 8th from the prompt's own hint reading "EUR 240,000"
  // under a budget of 2,400.
  const maxPrice = Math.round(minor / 100);

  if (!productId || !storeIdM) {
    return null;
  }

  // No usable price → no target. Never a zero or negative one, which would
  // match every quote.
  const hasPrice = Number.isFinite(maxPrice) && maxPrice > 0;

  const { data: result } = await axios.post("/api/core-list/pipeline", {
    store_id_m: storeIdM,
    product_id: productId,
    max_price: hasPrice ? maxPrice : null,
    currency: hasPrice ? data?.currency || "gbp" : null,
    // The watch's production year, when the client named one. Absent means the
    // backend watches from this year, which with "year and above" matching
    // covers everything newer.
    year: data?.year ? Number(data.year) : null,
    region: data?.watchRegion || null,
    // The note field was removed from the prompt (Alex, 2026-10-08); the
    // column stays for the marketplace sources that will use it.
    keywords: null,
    source_request_id: requestId || null,
  });

  return result;
};

export default createBuyAlertWatch;
